import { unzipSync } from "fflate";
import { DepositError, type DepositLimits } from "./limits.js";

export interface ZipEntry {
  path: string;
  data: Uint8Array;
}

export interface ZipContent {
  entries: ZipEntry[];
  /** Entries not read, with the reason (they stay inside the original ZIP). */
  skipped: Array<{ path: string; reason: string }>;
}

/** System files a ZIP made on macOS or Windows carries; never content. */
export function isSystemEntry(path: string): boolean {
  const parts = path.split("/").filter(Boolean);
  const base = parts.at(-1) ?? "";
  return parts.includes("__MACOSX") || base.startsWith(".") || ["Thumbs.db", "desktop.ini"].includes(base);
}

/**
 * Reads a ZIP in memory. Sizes declared by the ZIP are checked before anything
 * is decompressed (entry count, total and per-file size), and again after, so a
 * ZIP bomb or a lying header is refused rather than expanded.
 */
export function readZip(data: Uint8Array, limits: DepositLimits): ZipContent {
  const skipped: ZipContent["skipped"] = [];
  const declared = new Map<string, number>();
  let entries = 0;
  let total = 0;
  const issues: string[] = [];
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(data, {
      filter: (file) => {
        if (file.name.endsWith("/")) return false;
        entries += 1;
        if (isSystemEntry(file.name)) {
          skipped.push({ path: file.name, reason: "fichier système" });
          return false;
        }
        total += file.originalSize;
        if (file.originalSize > limits.maxFileBytes) {
          issues.push(`${file.name} : ${Math.ceil(file.originalSize / 1048576)} Mo une fois décompressé (limite ${Math.floor(limits.maxFileBytes / 1048576)} Mo)`);
          return false;
        }
        declared.set(file.name, file.originalSize);
        return true;
      },
    });
  } catch (error) {
    throw new DepositError("Ce ZIP ne peut pas être lu.", [String(error instanceof Error ? error.message : error)]);
  }
  if (entries > limits.maxZipEntries) issues.push(`${entries} fichiers dans le ZIP (limite ${limits.maxZipEntries})`);
  if (total > limits.maxZipUncompressedBytes) issues.push(`${Math.ceil(total / 1048576)} Mo une fois décompressé (limite ${Math.floor(limits.maxZipUncompressedBytes / 1048576)} Mo)`);
  if (issues.length) throw new DepositError("Ce ZIP est refusé.", issues);

  const result: ZipEntry[] = [];
  for (const [path, bytes] of Object.entries(files)) {
    if (bytes.length !== declared.get(path)) {
      throw new DepositError("Ce ZIP est refusé.", [`${path} : taille décompressée ${bytes.length}, annoncée ${declared.get(path)}`]);
    }
    if (bytes.length === 0) {
      skipped.push({ path, reason: "fichier vide" });
      continue;
    }
    result.push({ path, data: bytes });
  }
  return { entries: result, skipped };
}
